import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Save, Loader2, Plus, Trash2, Copy, GripVertical, ChevronDown, ChevronUp,
  Upload, ImageIcon, Eye, EyeOff, Info,
} from "lucide-react";

/**
 * Iter55 — Interactive Experiences CMS editor
 * ────────────────────────────────────────────────────────────────────────────
 * Same UX as HeroSlidesEditor but for the homepage_experiences section.
 * Reserves media types for future homepage content without a data-model
 * change (see EXPERIENCE_TYPES 'reserved' group).
 */

const EXPERIENCE_TYPES = [
  { value: "walkthrough",       label: "🎥 Walkthrough Video",        group: "live" },
  { value: "coohom",            label: "🏠 360° Coohom Tour",         group: "live" },
  { value: "matterport",        label: "🏡 Matterport Tour",           group: "live" },
  { value: "gallery_interior",  label: "📸 Interior Gallery",          group: "live" },
  { value: "gallery_exterior",  label: "📸 Exterior Gallery",          group: "live" },
  { value: "ai_video",          label: "🤖 AI Video",                 group: "live" },
  { value: "external_embed",    label: "🌐 External Embed",            group: "live" },
  { value: "drone",             label: "Drone Flythrough (reserved)",   group: "reserved" },
  { value: "construction",      label: "Construction Progress (reserved)", group: "reserved" },
  { value: "builder_showcase",  label: "Builder Showcase (reserved)",   group: "reserved" },
  { value: "interior_showcase", label: "Interior Designer (reserved)",  group: "reserved" },
  { value: "vr",                label: "VR Experience (reserved)",      group: "reserved" },
  { value: "testimonial",       label: "Client Testimonial (reserved)", group: "reserved" },
];

const CTA_TYPES = [
  { value: "internal",   label: "Internal Page",  placeholder: "/plans" },
  { value: "plan",       label: "House Plan",     placeholder: "plan-slug-or-id" },
  { value: "collection", label: "Collection",     placeholder: "collection-slug" },
  { value: "external",   label: "External URL",   placeholder: "https://…" },
  { value: "contact",    label: "Contact Form",   placeholder: "/contact" },
];

let _seq = 0;
const nextId = () => `exp-${Date.now().toString(36)}-${(_seq++).toString(36)}`;

const emptyItem = () => ({
  id: nextId(),
  experience_type: "walkthrough",
  title: "New experience",
  subtitle: "",
  thumbnail_url: "",
  media_url: "",
  media_provider_url: "",
  poster_url: "",
  related_plan_id: "",
  cta_type: "internal",
  cta_label: "",
  cta_href: "",
  enabled: true,
  order: 0,
  publish_at: "",
  expires_at: "",
  analytics: { impressions: 0, clicks: 0, cta_clicks: 0 },
});

const ExperiencesEditor = () => {
  const [section, setSection] = useState(null);
  const [items, setItems] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [dragIdx, setDragIdx] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/settings/all");
      const cms = data.homepage_experiences || {};
      setSection(cms.section || {
        eyebrow: "Explore Our Designs Before You Build",
        heading_line1: "See it,",
        heading_italic: "walk it, feel it —",
        heading_line3: "before you break ground.",
        description: "",
        footer_cta_label: "View all experiences",
        footer_cta_href: "/experiences",
      });
      setItems((cms.items || []).map((i) => ({ ...i, id: i.id || nextId() })));
    } catch (e) {
      toast.error("Failed to load Experiences", { description: e.response?.data?.detail || "" });
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!section || !items) return;
    setSaving(true);
    try {
      // Preserve order field
      const ordered = items.map((it, idx) => ({ ...it, order: idx }));
      await api.put("/admin/settings/homepage_experiences", { data: { section, items: ordered } });
      toast.success("Experiences saved", { description: "The homepage section will update on next page load." });
    } catch (e) {
      toast.error("Save failed", { description: e.response?.data?.detail || e.message });
    } finally { setSaving(false); }
  };

  const patch = (id, p) => setItems((prev) => prev.map((it) => (it.id === id ? { ...it, ...p } : it)));
  const addItem = () => {
    const it = emptyItem();
    setItems((prev) => [...prev, it]);
    setExpanded((e) => ({ ...e, [it.id]: true }));
  };
  const duplicate = (id) => {
    const src = items.find((i) => i.id === id);
    if (!src) return;
    const copy = { ...src, id: nextId(), title: `${src.title} (copy)` };
    const idx = items.findIndex((i) => i.id === id);
    setItems((prev) => {
      const next = prev.slice();
      next.splice(idx + 1, 0, copy);
      return next;
    });
    setExpanded((e) => ({ ...e, [copy.id]: true }));
  };
  const remove = (id) => setItems((prev) => prev.filter((i) => i.id !== id));

  const onDragStart = (i) => setDragIdx(i);
  const onDragOver = (e) => e.preventDefault();
  const onDrop = (i) => {
    if (dragIdx === null || dragIdx === i) return;
    setItems((prev) => {
      const next = prev.slice();
      const [moved] = next.splice(dragIdx, 1);
      next.splice(i, 0, moved);
      return next;
    });
    setDragIdx(null);
  };

  const uploadFile = async (id, file, kind /* image | video */) => {
    if (!file) return;
    const cap = kind === "video" ? 25 * 1024 * 1024 : 8 * 1024 * 1024;
    if (file.size > cap) {
      toast.error("File too large");
      return;
    }
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/uploads?kind=${kind}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (kind === "image") patch(id, { thumbnail_url: data.url });
      else patch(id, { media_url: data.url });
      toast.success(`${kind === "image" ? "Thumbnail" : "Video"} uploaded`);
    } catch (e) {
      toast.error("Upload failed", { description: e.response?.data?.detail || e.message });
    }
  };

  if (!section || !items) return <div className="text-muted-foreground">Loading…</div>;

  return (
    <div className="max-w-4xl space-y-8" data-testid="experiences-editor">
      {/* Section header content */}
      <div className="border border-border p-5 bg-card" data-testid="experiences-section-config">
        <div className="flex items-start gap-3 mb-5">
          <Info size={16} className="text-accent shrink-0 mt-0.5" />
          <div>
            <h3 className="font-display text-xl">Section content</h3>
            <p className="text-sm text-muted-foreground mt-0.5">Headline + description shown above the experience grid.</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div><Label className="overline text-muted-foreground text-xs">Eyebrow</Label>
            <Input value={section.eyebrow || ""} onChange={(e) => setSection({ ...section, eyebrow: e.target.value })} className="rounded-none h-10 mt-1" data-testid="exp-section-eyebrow" />
          </div>
          <div><Label className="overline text-muted-foreground text-xs">Heading line 1</Label>
            <Input value={section.heading_line1 || ""} onChange={(e) => setSection({ ...section, heading_line1: e.target.value })} className="rounded-none h-10 mt-1" data-testid="exp-section-line1" />
          </div>
          <div><Label className="overline text-muted-foreground text-xs">Heading italic</Label>
            <Input value={section.heading_italic || ""} onChange={(e) => setSection({ ...section, heading_italic: e.target.value })} className="rounded-none h-10 mt-1" data-testid="exp-section-italic" />
          </div>
          <div className="md:col-span-3"><Label className="overline text-muted-foreground text-xs">Heading line 3</Label>
            <Input value={section.heading_line3 || ""} onChange={(e) => setSection({ ...section, heading_line3: e.target.value })} className="rounded-none h-10 mt-1" data-testid="exp-section-line3" />
          </div>
          <div className="md:col-span-3">
            <Label className="overline text-muted-foreground text-xs">Description</Label>
            <Textarea rows={2} value={section.description || ""} onChange={(e) => setSection({ ...section, description: e.target.value })} className="rounded-none mt-1" data-testid="exp-section-description" />
          </div>
          <div><Label className="overline text-muted-foreground text-xs">Footer CTA label</Label>
            <Input value={section.footer_cta_label || ""} onChange={(e) => setSection({ ...section, footer_cta_label: e.target.value })} className="rounded-none h-10 mt-1" data-testid="exp-section-cta-label" />
          </div>
          <div><Label className="overline text-muted-foreground text-xs">Footer CTA link</Label>
            <Input value={section.footer_cta_href || ""} onChange={(e) => setSection({ ...section, footer_cta_href: e.target.value })} className="rounded-none h-10 mt-1" data-testid="exp-section-cta-href" />
          </div>
        </div>
      </div>

      {/* Items */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-display text-xl">Experiences <span className="text-muted-foreground text-sm ml-2">{items.length}</span></h3>
          <p className="text-sm text-muted-foreground mt-0.5">Drag to reorder. Card grid renders in this order on the homepage.</p>
        </div>
        <Button onClick={addItem} variant="outline" className="rounded-none h-10" data-testid="exp-add"><Plus size={14} className="mr-2" />Add experience</Button>
      </div>

      {items.length === 0 && (
        <div className="border border-dashed border-border p-10 text-center text-muted-foreground text-sm">
          No experiences yet. Add walkthrough videos, 360° tours, galleries and more to bring this section to life.
        </div>
      )}

      <div className="space-y-3">
        {items.map((item, idx) => (
          <ExperienceRow
            key={item.id}
            item={item}
            index={idx}
            expanded={!!expanded[item.id]}
            onToggleExpand={() => setExpanded((e) => ({ ...e, [item.id]: !e[item.id] }))}
            onPatch={(p) => patch(item.id, p)}
            onDuplicate={() => duplicate(item.id)}
            onDelete={() => remove(item.id)}
            onDragStart={() => onDragStart(idx)}
            onDragOver={onDragOver}
            onDrop={() => onDrop(idx)}
            isDragging={dragIdx === idx}
            uploadThumb={(f) => uploadFile(item.id, f, "image")}
            uploadVideo={(f) => uploadFile(item.id, f, "video")}
          />
        ))}
      </div>

      <div className="sticky bottom-4 z-10 flex items-center justify-between border border-border bg-background/95 backdrop-blur px-4 py-3">
        <div className="text-xs text-muted-foreground">Save publishes the full Experiences section to the live homepage.</div>
        <Button onClick={save} disabled={saving} className="rounded-none bg-primary h-11 px-8" data-testid="exp-save">
          {saving ? <><Loader2 size={14} className="mr-2 animate-spin" />Saving…</> : <><Save size={14} className="mr-2" />Save Experiences</>}
        </Button>
      </div>
    </div>
  );
};

// ── One experience row ──────────────────────────────────────────────────────
const ExperienceRow = ({ item, index, expanded, onToggleExpand, onPatch, onDuplicate, onDelete, onDragStart, onDragOver, onDrop, isDragging, uploadThumb, uploadVideo }) => {
  const thumbRef = useRef(null);
  const videoRef = useRef(null);
  const meta = EXPERIENCE_TYPES.find((t) => t.value === item.experience_type) || EXPERIENCE_TYPES[0];
  const ctaMeta = CTA_TYPES.find((t) => t.value === (item.cta_type || "internal")) || CTA_TYPES[0];

  return (
    <div
      draggable
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      data-testid={`exp-row-${index}`}
      className={`border bg-card ${isDragging ? "opacity-40" : ""} ${item.enabled === false ? "border-dashed border-muted-foreground/40" : "border-border"}`}
    >
      <div className="flex items-center gap-3 p-3">
        <span className="cursor-grab text-muted-foreground shrink-0 p-1"><GripVertical size={16} /></span>
        <div className="w-16 h-10 bg-muted overflow-hidden shrink-0 border border-border flex items-center justify-center">
          {item.thumbnail_url ? (
            <img src={item.thumbnail_url} alt="" className="w-full h-full object-cover" />
          ) : (
            <ImageIcon size={14} className="text-muted-foreground" />
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs text-muted-foreground uppercase tracking-[0.16em]">{meta.label}</div>
          <div className="font-medium text-sm truncate" data-testid={`exp-title-${index}`}>{item.title || <span className="italic text-muted-foreground">Untitled</span>}</div>
        </div>
        <button onClick={() => onPatch({ enabled: !(item.enabled !== false) })} className="p-1.5 text-muted-foreground hover:text-foreground" data-testid={`exp-toggle-${index}`}>
          {item.enabled !== false ? <Eye size={16} /> : <EyeOff size={16} />}
        </button>
        <button onClick={onDuplicate} className="p-1.5 text-muted-foreground hover:text-foreground" data-testid={`exp-duplicate-${index}`}><Copy size={16} /></button>
        <button onClick={onDelete} className="p-1.5 text-muted-foreground hover:text-destructive" data-testid={`exp-delete-${index}`}><Trash2 size={16} /></button>
        <button onClick={onToggleExpand} className="p-1.5 text-muted-foreground hover:text-foreground" data-testid={`exp-expand-${index}`}>
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </button>
      </div>

      {expanded && (
        <div className="border-t border-border p-5 space-y-5" data-testid={`exp-body-${index}`}>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-3">
              <div>
                <Label className="overline text-muted-foreground text-xs">Experience type</Label>
                <Select value={item.experience_type} onValueChange={(v) => onPatch({ experience_type: v })}>
                  <SelectTrigger className="rounded-none h-10 mt-1" data-testid={`exp-type-${index}`}><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {EXPERIENCE_TYPES.filter((t) => t.group === "live").map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                    <div className="px-2 py-1.5 text-[10px] uppercase tracking-[0.16em] text-muted-foreground border-t border-border mt-1">Reserved for future content</div>
                    {EXPERIENCE_TYPES.filter((t) => t.group === "reserved").map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="overline text-muted-foreground text-xs">Title</Label>
                <Input value={item.title || ""} onChange={(e) => onPatch({ title: e.target.value })} className="rounded-none h-10 mt-1" data-testid={`exp-title-input-${index}`} />
              </div>
              <div>
                <Label className="overline text-muted-foreground text-xs">Subtitle</Label>
                <Input value={item.subtitle || ""} onChange={(e) => onPatch({ subtitle: e.target.value })} className="rounded-none h-10 mt-1" data-testid={`exp-subtitle-${index}`} />
              </div>
              <div>
                <Label className="overline text-muted-foreground text-xs">Related plan ID (optional)</Label>
                <Input value={item.related_plan_id || ""} onChange={(e) => onPatch({ related_plan_id: e.target.value })} className="rounded-none h-10 mt-1" placeholder="plan-uuid or slug" data-testid={`exp-plan-${index}`} />
              </div>
            </div>
            <div className="space-y-3">
              <div>
                <Label className="overline text-muted-foreground text-xs">Thumbnail (shown on the card)</Label>
                <div className="mt-1 aspect-video bg-muted border border-border overflow-hidden flex items-center justify-center">
                  {item.thumbnail_url ? <img src={item.thumbnail_url} alt="" className="w-full h-full object-cover" /> : <ImageIcon size={22} className="text-muted-foreground" />}
                </div>
                <input type="file" ref={thumbRef} accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => e.target.files?.[0] && uploadThumb(e.target.files[0])} data-testid={`exp-thumb-file-${index}`} />
                <div className="mt-2 flex gap-2">
                  <Button onClick={() => thumbRef.current?.click()} className="rounded-none h-9 bg-primary" data-testid={`exp-thumb-upload-${index}`}><Upload size={14} className="mr-2" />Upload thumbnail</Button>
                  {item.thumbnail_url && <Button variant="outline" onClick={() => onPatch({ thumbnail_url: "" })} className="rounded-none h-9">Clear</Button>}
                </div>
              </div>

              {(item.experience_type === "walkthrough" || item.experience_type === "ai_video" || item.experience_type === "drone" || item.experience_type === "construction") && (
                <div>
                  <Label className="overline text-muted-foreground text-xs">Video (MP4 upload or YouTube / Vimeo URL)</Label>
                  <input type="file" ref={videoRef} accept="video/mp4" className="hidden" onChange={(e) => e.target.files?.[0] && uploadVideo(e.target.files[0])} data-testid={`exp-video-file-${index}`} />
                  <div className="mt-1 flex gap-2">
                    <Button onClick={() => videoRef.current?.click()} className="rounded-none h-9 bg-primary" data-testid={`exp-video-upload-${index}`}><Upload size={14} className="mr-2" />Upload MP4</Button>
                    {item.media_url && <Button variant="outline" onClick={() => onPatch({ media_url: "" })} className="rounded-none h-9">Clear</Button>}
                  </div>
                  <Input value={item.media_provider_url || ""} onChange={(e) => onPatch({ media_provider_url: e.target.value })} placeholder="…or paste a YouTube / Vimeo URL" className="rounded-none h-10 mt-2" data-testid={`exp-provider-url-${index}`} />
                </div>
              )}

              {["coohom","matterport","external_embed","vr"].includes(item.experience_type) && (
                <div>
                  <Label className="overline text-muted-foreground text-xs">Embed URL</Label>
                  <Input value={item.media_provider_url || ""} onChange={(e) => onPatch({ media_provider_url: e.target.value })} placeholder="https://my.matterport.com/show/?m=… or https://www.coohom.com/…" className="rounded-none h-10 mt-1" data-testid={`exp-provider-url-${index}`} />
                </div>
              )}

              {["gallery_interior","gallery_exterior","builder_showcase","interior_showcase"].includes(item.experience_type) && (
                <div>
                  <Label className="overline text-muted-foreground text-xs">Gallery image URL</Label>
                  <Input value={item.media_url || ""} onChange={(e) => onPatch({ media_url: e.target.value })} placeholder="https://… (multi-image gallery ships in a later iteration)" className="rounded-none h-10 mt-1" data-testid={`exp-gallery-url-${index}`} />
                </div>
              )}
            </div>
          </div>

          {/* CTA */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 border-t border-border pt-5">
            <div>
              <Label className="overline text-muted-foreground text-xs">CTA label</Label>
              <Input value={item.cta_label || ""} onChange={(e) => onPatch({ cta_label: e.target.value })} placeholder="View the plan" className="rounded-none h-10 mt-1" data-testid={`exp-cta-label-${index}`} />
            </div>
            <div>
              <Label className="overline text-muted-foreground text-xs">CTA type</Label>
              <Select value={item.cta_type || "internal"} onValueChange={(v) => onPatch({ cta_type: v })}>
                <SelectTrigger className="rounded-none h-10 mt-1" data-testid={`exp-cta-type-${index}`}><SelectValue /></SelectTrigger>
                <SelectContent>{CTA_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="overline text-muted-foreground text-xs">CTA link</Label>
              <Input value={item.cta_href || ""} onChange={(e) => onPatch({ cta_href: e.target.value })} placeholder={ctaMeta.placeholder} className="rounded-none h-10 mt-1" data-testid={`exp-cta-href-${index}`} />
            </div>
          </div>

          {/* Scheduling */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 border-t border-border pt-5">
            <div>
              <Label className="overline text-muted-foreground text-xs">Publish at</Label>
              <Input type="datetime-local" value={item.publish_at || ""} onChange={(e) => onPatch({ publish_at: e.target.value })} className="rounded-none h-10 mt-1" data-testid={`exp-publish-at-${index}`} />
            </div>
            <div>
              <Label className="overline text-muted-foreground text-xs">Expires at</Label>
              <Input type="datetime-local" value={item.expires_at || ""} onChange={(e) => onPatch({ expires_at: e.target.value })} className="rounded-none h-10 mt-1" data-testid={`exp-expires-at-${index}`} />
            </div>
          </div>

          {/* Analytics */}
          <div className="border-t border-border pt-5" data-testid={`exp-analytics-${index}`}>
            <div className="overline text-accent text-xs mb-3">Analytics</div>
            <div className="grid grid-cols-3 gap-3">
              <div className="border border-border p-3">
                <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Impressions</div>
                <div className="font-display text-2xl mt-1 tabular-nums" data-testid={`exp-impressions-${index}`}>{item.analytics?.impressions ?? 0}</div>
              </div>
              <div className="border border-border p-3">
                <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Card clicks</div>
                <div className="font-display text-2xl mt-1 tabular-nums" data-testid={`exp-clicks-${index}`}>{item.analytics?.clicks ?? 0}</div>
              </div>
              <div className="border border-border p-3">
                <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">CTA clicks</div>
                <div className="font-display text-2xl mt-1 tabular-nums" data-testid={`exp-cta-clicks-${index}`}>{item.analytics?.cta_clicks ?? 0}</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ExperiencesEditor;
